// Implementation Note: Default sort is Category, then Alphabetical within each group,
// matching the template editor's default item order in ItemLocationFolders.
//
// Unit fields are read-only — they come from the existing item's unit and are
// passed through unchanged in the save payload. Users can only set the numeric
// PAR level here; units are edited in the template editor.
//
// The wizard asks for a PAR and nothing else. It used to carry a second column
// for a minimum order quantity, auto-filled from the PAR's fractional part —
// but a PAR belongs to the ITEM and a minimum belongs to a VENDOR PRODUCT, so
// setting a PAR must work with zero vendor knowledge and no MOQ may be required
// to complete it. Vendor minimums live with the vendor's purchase options and
// are applied in Savings Review.
//
// Only rows whose par_level differs from the original are included in the
// onSave payload, reducing write volume.
//
// Deduplication: PARs are global per canonical item, so items that appear in
// multiple locations — or under multiple vendors — are collapsed to a single
// row by dedup key (item_id when present, else normalized name only).
// The save fans out to every underlying count_item id so all locations are
// updated atomically. The location chip is suppressed for deduped rows since
// showing one arbitrary location would be misleading.
//
// Mobile numeric keypad: on touch-primary devices (phones, tablets, Expo WebView)
// the PAR input is read-only — tapping activates it and drives its value
// from an in-popup 3×4 keypad docked at the bottom of the panel so the OS
// keyboard is never raised. Desktop keeps normal typed input.
//
// Count-mode props (the wizard doubles as the in-count PAR editor):
// - excludeItemsWithPar hides rows that already had a PAR when the wizard
//   opened (judged from the initial values, so rows never vanish mid-edit).
// - anchorCountItemId lands the cursor on the item the user was counting when
//   they tapped its PAR badge; when that row is scrolled out of view a floating
//   "Back to <item>" pill snaps back to it.
// - hideSkip removes the Skip button; confirmMissingPars=false saves without
//   the "some items still need a par" confirmation.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { activeOptions, distinctVendorCount, vendorLabelOf } from '../utils/itemCostSummary';
import { fetchAliasMap, matchAliases } from '../services/itemAliasService';

const SORT_OPTIONS = [
  { value: 'category', label: 'Category' },
  { value: 'location', label: 'Location' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'alphabetical', label: 'Alphabetical' },
];

// PAR color tokens — match the template editor (ItemLocationFolders)
const PAR_BG = '#fff3cd';
const PAR_BORDER = '#f0ad4e';

// item | status badge | PAR. There is no fourth column: the wizard collects a
// PAR and nothing else.
const ROW_GRID_COLUMNS = 'minmax(0,1fr) 44px 140px';

// The overlay is sized to window.visualViewport so the panel always lives
// inside the area not covered by the on-screen keyboard, and the panel itself
// just fills its parent (minus safe-area padding).
const PARS_WIZARD_STYLES = `
  .pars-wizard-par-input::placeholder { color: #C8A86B; opacity: 1; }
  .pars-wizard-field-active { box-shadow: 0 0 0 2px #1976D2 !important; }
`;

function truncateMeta(str, max = 50) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max - 1) + '\u2026' : str;
}

function buildOriginals(items) {
  const map = {};
  (items || []).forEach(item => {
    map[item.id] = {
      parLevel: (item.par_level != null && item.par_level !== '') ? String(item.par_level) : '',
      parUnit: item.par_unit || item.item_snapshot?.unit || 'each',
    };
  });
  return map;
}

// Compute a dedup key for an item — item_id when available (most reliable),
// otherwise the normalized name alone: a par is global per canonical item, so
// the same product from different vendors is ONE row, not one row per vendor.
function dedupKey(item) {
  if (item.item_id) return `id:${item.item_id}`;
  const name = (item.item_snapshot?.description || item.item_snapshot?.name || '').trim().toLowerCase();
  return `nv:${name}`;
}

// Collapse a flat list of count_items into one representative row per distinct
// item. Each row gains a `siblingIds` array (all count_item ids sharing the
// same dedup key) and an `isDeduped` boolean (true when siblingIds.length > 1).
// For non-first-time setup we pick the representative that already has a par
// so the input is pre-filled; otherwise the first-seen item wins.
function buildDedupedRows(items, originals, isFirstTimeSetup) {
  const keyOrder = [];
  const siblingMap = new Map();   // key -> [count_item ids]
  const repMap = new Map();        // key -> representative item

  (items || []).forEach(item => {
    const key = dedupKey(item);
    if (!siblingMap.has(key)) {
      siblingMap.set(key, []);
      repMap.set(key, item);
      keyOrder.push(key);
    }
    siblingMap.get(key).push(item.id);
    // Prefer a representative that already has a par so the input is pre-filled.
    const existing = repMap.get(key);
    if (!isFirstTimeSetup && Number(item.par_level) > 0 && !(Number(existing.par_level) > 0)) {
      repMap.set(key, item);
    }
  });

  return keyOrder.map(key => {
    const item = repMap.get(key);
    const siblingIds = siblingMap.get(key);
    const isDeduped = siblingIds.length > 1;
    const orig = originals[item.id] || {};
    return {
      id: item.id,
      itemId: item.item_id ?? null,
      siblingIds,
      isDeduped,
      name: (item.item_snapshot?.description || item.item_snapshot?.name || '(Unknown)').trim(),
      category: item.item_snapshot?.category || item.item_snapshot?.item_category || item.category || '',
      location: isDeduped ? '' : (item.location || ''),
      vendor: item.item_snapshot?.vendor_name || item.item_snapshot?.vendor || item.vendor || '',
      parLevel: isFirstTimeSetup ? '' : (orig.parLevel ?? ''),
      parUnit: orig.parUnit ?? 'each',
    };
  });
}

// Vendor segment of the row meta. PARs are global per canonical item, so the
// vendor shown comes from the item's ACTIVE purchase options: 'Multiple
// vendors (n)' at ≥2 distinct vendors, the single vendor name at 1, nothing
// at 0. Before the options fetch lands (or when it fails / the row has no
// item FK), fall back to the snapshot vendor so the meta never flashes empty.
function vendorMetaFor(row, optionsByItemId) {
  if (!optionsByItemId || row.itemId == null) return row.vendor || '';
  const options = optionsByItemId[row.itemId] || [];
  const active = activeOptions(options);
  // Count vendors the same way every other multi-vendor surface does
  // (vendor_id → vendor_name_text → vendor_description). Counting distinct
  // vendor_description alone over-counted: on imported rows that column holds
  // the vendor's PRODUCT name, so one supplier's three packs read as three
  // vendors.
  const vendorCount = distinctVendorCount(active);
  if (vendorCount >= 2) return `Multiple vendors (${vendorCount})`;
  if (vendorCount === 1) return vendorLabelOf(active[0], {}) || row.vendor || '';
  if (active.length === 0) return '';
  return row.vendor || '';
}

// Detect touch-primary devices (phones, tablets, Expo WebView). Returns true
// when the primary pointing device is coarse (finger), which is the right
// signal to suppress the OS keyboard and show our in-popup keypad instead.
// Evaluated once at mount; never changes during a session.
function detectMobileKeypad() {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch (_) {
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  }
}

// Append a character to a numeric string, enforcing decimal rules.
function appendToNumericStr(current, char) {
  if (char === '.') {
    if (current.includes('.')) return current; // only one decimal point
    // Leading dot → prepend 0
    return (current === '' ? '0' : current) + '.';
  }
  // Skip leading zeros before non-zero digits (e.g. "07" → "7")
  if (current === '0' && char !== '.') return char;
  return current + char;
}

export default function ParsWizardModal({
  template, items, isFirstTimeSetup, storeId: storeIdProp = null,
  excludeItemsWithPar = false, anchorCountItemId = null,
  hideSkip = false, confirmMissingPars = true,
  onSave, onSkip, onCancel,
  // Accepted and ignored. `parsOnly` used to hide the MOQ column and
  // `initialFocusField` could land the cursor in it; every wizard is now
  // pars-only, so callers may keep passing them without effect.
  parsOnly = false, initialFocusField = 'parLevel', // eslint-disable-line no-unused-vars
}) {
  const originals = useMemo(() => buildOriginals(items), []); // eslint-disable-line react-hooks/exhaustive-deps

  // On first-time setup the inputs start blank so the placeholder ("0" / "—")
  // shows and the user can type without first having to delete a default value.
  // After pars have been set, the actual stored values are pre-filled for editing.
  // Items are deduped so multi-location items appear exactly once.
  const [initialRows] = useState(() => buildDedupedRows(items, originals, isFirstTimeSetup));
  const [rows, setRows] = useState(initialRows);

  // Row ids that had NO par when the wizard opened. excludeItemsWithPar
  // filters against this frozen set — not the live values — so a row the user
  // is typing a PAR into never vanishes from under their cursor.
  const noParRowIds = useMemo(() => {
    const set = new Set();
    initialRows.forEach(r => { if (!(parseFloat(r.parLevel) > 0)) set.add(r.id); });
    return set;
  }, [initialRows]);

  // The deduped row representing the count_item the user was on when they
  // opened the wizard (anchorCountItemId may be any sibling id of that row).
  const anchorRowId = useMemo(() => {
    if (anchorCountItemId == null) return null;
    const row = initialRows.find(r => r.id === anchorCountItemId || (r.siblingIds || []).includes(anchorCountItemId));
    return row ? row.id : null;
  }, [initialRows, anchorCountItemId]);
  const anchorRow = anchorRowId != null ? rows.find(r => r.id === anchorRowId) : null;

  // True while the anchor row is scrolled out of the visible list, which is
  // when the "Back to <item>" pill shows.
  const [anchorOffscreen, setAnchorOffscreen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('category');
  const [saving, setSaving] = useState(false);
  const [confirmMissing, setConfirmMissing] = useState(null);

  // Store id for the purchase-options / alias lookups. Callers don't pass it,
  // so fall back to the signed-in store in localStorage.
  const [storeId] = useState(() => {
    if (storeIdProp) return storeIdProp;
    try { return JSON.parse(localStorage.getItem('currentStore') || 'null')?.id ?? null; } catch (_) { return null; }
  });

  // null = options not loaded yet (or load failed) → vendor meta falls back to
  // the item snapshot's vendor, i.e. today's behavior.
  const [optionsByItemId, setOptionsByItemId] = useState(null);
  const [aliasMap, setAliasMap] = useState({});

  // Touch-primary device: suppress OS keyboard and use the in-popup keypad.
  // Stable for the lifetime of the component (detected once at mount).
  const [isMobileKeypad] = useState(detectMobileKeypad);

  // Which row's PAR input is currently active in the mobile keypad.
  // { rowId: number, field: 'parLevel' } | null
  const [focusedField, setFocusedField] = useState(null);

  // Refs for each item row so we can scroll the active row into view.
  const rowRefs = useRef({});
  const rowListRef = useRef(null);
  // Refs for the desktop number inputs, keyed `${rowId}:${field}`, so the
  // anchor row's field can take the cursor when the wizard opens.
  const inputRefs = useRef({});

  // Tracks the visible viewport so we can pin the modal inside the area that
  // isn't covered by the on-screen keyboard, including the offsetTop value
  // iOS Safari uses when it scrolls the layout viewport up to make room for
  // the keyboard. Falls back to window.innerHeight when the visualViewport
  // API is not available (older browsers / desktop).
  const [viewport, setViewport] = useState(() => {
    if (typeof window === 'undefined') return { height: 0, offsetTop: 0 };
    const vv = window.visualViewport;
    return {
      height: (vv && vv.height) || window.innerHeight || 0,
      offsetTop: (vv && vv.offsetTop) || 0,
    };
  });
  const searchRef = useRef(null);

  useEffect(() => {
    // Only auto-focus the search bar on desktop; on mobile the keypad is the
    // primary input method and focusing the search box would open the OS keyboard.
    // With an anchor item the cursor belongs on that row instead (below).
    if (!isMobileKeypad && anchorRowId == null) {
      setTimeout(() => searchRef.current?.focus(), 80);
    }
  }, [isMobileKeypad, anchorRowId]);

  // Land on the anchor item: center its row in the list and put the cursor on
  // the field the user tapped (mobile: activate it in the keypad; desktop:
  // focus the real input). Runs once — anchorRowId is frozen at mount.
  useEffect(() => {
    if (anchorRowId == null) return undefined;
    const field = 'parLevel';
    if (isMobileKeypad) {
      setFocusedField({ rowId: anchorRowId, field });
    }
    const timer = setTimeout(() => {
      const el = rowRefs.current[anchorRowId];
      if (el) {
        try { el.scrollIntoView({ block: 'center' }); } catch (_) { try { el.scrollIntoView(); } catch (__) {} }
      }
      if (!isMobileKeypad) {
        const input = inputRefs.current[`${anchorRowId}:${field}`];
        if (input) {
          try { input.focus({ preventScroll: true }); input.select?.(); } catch (_) {}
        }
      }
    }, 60);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const snapBackToAnchor = () => {
    const el = anchorRowId != null ? rowRefs.current[anchorRowId] : null;
    if (!el) return;
    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { try { el.scrollIntoView(); } catch (__) {} }
  };

  // Load this store's purchase options (for the vendor meta segment) and item
  // aliases (for alias-aware search) once per mount. Both are non-fatal: when
  // the backing tables/migrations are not deployed the wizard quietly keeps
  // its previous behavior (snapshot vendor meta, name-only search).
  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    const itemIds = [...new Set(rows.map(r => r.itemId).filter(id => id != null))];
    fetchOptionsForItems(storeId, itemIds)
      .then((grouped) => { if (!cancelled) setOptionsByItemId(grouped || {}); })
      .catch((err) => { console.warn('[pars-wizard] purchase options unavailable:', err?.message || err); });
    fetchAliasMap(storeId)
      .then((map) => { if (!cancelled) setAliasMap(map || {}); })
      .catch((err) => { console.warn('[pars-wizard] item aliases unavailable:', err?.message || err); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to visualViewport resize/scroll events — these fire when the soft
  // keyboard opens/closes (resize) and when iOS scrolls the layout viewport
  // to keep the focused input visible (scroll, which moves offsetTop).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const handler = () => setViewport({ height: vv.height, offsetTop: vv.offsetTop });
    vv.addEventListener('resize', handler);
    vv.addEventListener('scroll', handler);
    handler();
    return () => {
      vv.removeEventListener('resize', handler);
      vv.removeEventListener('scroll', handler);
    };
  }, []);

  // When a number input is focused (desktop only), scroll it into view so the
  // user can see what they're typing above the keyboard. Use a small delay so
  // the keyboard animation finishes (and the visualViewport resize fires)
  // before we scroll.
  const handleInputFocus = (e) => {
    const el = e.currentTarget;
    setTimeout(() => {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {
        try { el.scrollIntoView(); } catch (__) {}
      }
    }, 250);
  };

  useEffect(() => {
    // Track whether we added the class so cleanup never removes it when it
    // was already present before this modal mounted (e.g. onboarding pages
    // that suppress the nav independently).
    let addedNavHidden = false;
    if (typeof document !== 'undefined' && !document.documentElement.classList.contains('mobile-nav-hidden')) {
      document.documentElement.classList.add('mobile-nav-hidden');
      addedNavHidden = true;
    }
    try {
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_show' }));
      }
    } catch (_) {}
    return () => {
      if (addedNavHidden && typeof document !== 'undefined') {
        document.documentElement.classList.remove('mobile-nav-hidden');
      }
      try {
        if (typeof window !== 'undefined' && window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'modal_backdrop_hide' }));
        }
      } catch (_) {}
    };
  }, []);

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  // Alias hits for the current query: itemId → matched alias. Lets the search
  // find items by a vendor's wording ('whl milk' → Whole Milk).
  const aliasHitByItemId = useMemo(() => {
    const map = new Map();
    matchAliases(aliasMap, search).forEach((m) => {
      if (!map.has(m.itemId)) map.set(m.itemId, m.alias);
    });
    return map;
  }, [aliasMap, search]);

  const sortedFilteredRows = useMemo(() => {
    let filtered = rows;
    // Count-mode filter: only items that still had no PAR when the wizard
    // opened (frozen set — typing a PAR must not remove the row). The anchor
    // row is always kept so the user's item can't be filtered out from under
    // them.
    if (excludeItemsWithPar) {
      filtered = filtered.filter(r => noParRowIds.has(r.id) || r.id === anchorRowId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(r => r.name.toLowerCase().includes(q) || (r.itemId != null && aliasHitByItemId.has(r.itemId)));
    }
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'category') cmp = (a.category || '').localeCompare(b.category || '');
      else if (sortBy === 'location') cmp = (a.location || '').localeCompare(b.location || '');
      else if (sortBy === 'vendor') cmp = (a.vendor || '').localeCompare(b.vendor || '');
      if (cmp !== 0) return cmp;
      return a.name.localeCompare(b.name);
    });
  }, [rows, search, sortBy, aliasHitByItemId, excludeItemsWithPar, noParRowIds, anchorRowId]);

  // Whether the anchor row is currently rendered at all (search can hide it);
  // the snap-back pill only makes sense while the row exists in the list.
  const anchorRowRendered = anchorRowId != null && sortedFilteredRows.some(r => r.id === anchorRowId);

  // Watch whether the anchor row is inside the visible part of the list; the
  // snap-back pill renders only while it is not. Re-arms when filtering or
  // sorting re-renders the list (the row's DOM node can be replaced).
  useEffect(() => {
    if (anchorRowId == null || typeof IntersectionObserver === 'undefined') return undefined;
    const rootEl = rowListRef.current;
    const el = rowRefs.current[anchorRowId];
    if (!rootEl || !el) { setAnchorOffscreen(false); return undefined; }
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach(entry => setAnchorOffscreen(!entry.isIntersecting)); },
      { root: rootEl, threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [anchorRowId, sortedFilteredRows]);

  // ─── Mobile keypad helpers ────────────────────────────────────────────────

  // Activate a field in the mobile keypad and scroll its row into view.
  const activateField = (rowId, field) => {
    setFocusedField({ rowId, field });
    setTimeout(() => {
      const el = rowRefs.current[rowId];
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 40);
  };

  // Look up the current value / unit for the focused field.
  const focusedRow = focusedField ? rows.find(r => r.id === focusedField?.rowId) : null;
  const focusedValue = focusedRow ? (focusedRow[focusedField.field] ?? '') : '';
  const focusedUnit = focusedRow ? (focusedRow.parUnit || 'each') : '';

  // Handle a keypad button press (digit, '.', or '⌫').
  const handleKeypadPress = (key) => {
    if (!focusedField) return;
    const { rowId, field } = focusedField;
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    const current = row[field] ?? '';
    let next;
    if (key === '⌫') {
      next = current.slice(0, -1);
    } else {
      next = appendToNumericStr(current, key);
    }
    updateRow(rowId, field, next);
  };

  // Move to the next row's PAR.
  const moveToNextField = () => {
    if (!focusedField) return;
    const { rowId } = focusedField;
    const currentIdx = sortedFilteredRows.findIndex(r => r.id === rowId);
    const nextRow = sortedFilteredRows[currentIdx + 1];
    if (nextRow) {
      activateField(nextRow.id, 'parLevel');
    } else {
      // Last row — dismiss focus
      setFocusedField(null);
    }
  };

  // ─── Save logic ───────────────────────────────────────────────────────────

  const handleSaveClick = () => {
    // Count mode saves without the completeness nag — the user came to edit
    // one item's PAR, not to finish setting up the whole template.
    if (confirmMissingPars) {
      const missingCount = rows.filter(r => !r.parLevel || parseFloat(r.parLevel) <= 0).length;
      if (missingCount > 0) {
        setConfirmMissing({ count: missingCount, total: rows.length });
        return;
      }
    }
    doSave();
  };

  // Build a flat payload entry for one count_item id from the row's current
  // values. PAR only — no min_order_qty / min_order_unit reaches a caller, so
  // no caller can write a vendor minimum on the strength of a PAR edit.
  const rowToPayload = (r, sibId) => ({
    id: sibId,
    par_level: (r.parLevel !== '' && r.parLevel != null) ? (parseFloat(r.parLevel) || null) : null,
    par_unit: r.parUnit || 'each',
  });

  const doSave = async () => {
    setSaving(true);
    try {
      // changedRows: only rows whose par changed, fanned out to all sibling ids.
      const changedRows = rows
        .filter(r => {
          const o = originals[r.id];
          if (!o) return true;
          return r.parLevel !== o.parLevel;
        })
        .flatMap(r => (r.siblingIds && r.siblingIds.length > 0 ? r.siblingIds : [r.id]).map(sibId => rowToPayload(r, sibId)));

      // allRows: every deduped row fanned out to all sibling ids, for cache updates.
      const allRows = rows.flatMap(r =>
        (r.siblingIds && r.siblingIds.length > 0 ? r.siblingIds : [r.id]).map(sibId => rowToPayload(r, sibId))
      );

      await onSave(changedRows, allRows);
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  // Size the overlay to the visible viewport (the area not occluded by the
  // on-screen keyboard) and shift it down by visualViewport.offsetTop so it
  // tracks iOS's layout-viewport scroll. Combined with alignItems: flex-start
  // and a safe-area top padding, this keeps the panel below the iPhone status
  // bar / notch even when the keyboard is open.
  const visiblePx = viewport.height > 0 ? viewport.height : (typeof window !== 'undefined' ? window.innerHeight : 0);
  const overlayHeight = visiblePx > 0 ? `${visiblePx}px` : '100vh';

  const overlayStyle = {
    position: 'fixed',
    top: viewport.offsetTop > 0 ? `${viewport.offsetTop}px` : 0,
    left: 0,
    right: 0,
    height: overlayHeight,
    backgroundColor: 'rgba(10,15,25,0.6)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 'calc(env(safe-area-inset-top, 0px) + 12px) 12px calc(env(safe-area-inset-bottom, 0px) + 12px)',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const panelStyle = {
    backgroundColor: '#FDFBF7', borderRadius: '14px', border: '1px solid #D9CEC2',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)', width: '100%', maxWidth: '640px',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: '100%',
    minHeight: 0,
  };

  const confirmOverlayStyle = { ...overlayStyle, alignItems: 'center', zIndex: 3100 };

  if (confirmMissing) {
    return (
      <div onClick={handleBackdropClick} style={confirmOverlayStyle}>
        <style>{PARS_WIZARD_STYLES}</style>
        <div onClick={e => e.stopPropagation()} style={{
          backgroundColor: '#FDFBF7', borderRadius: '14px', border: '1px solid #D9CEC2',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: '400px', width: '100%',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          maxHeight: '100%',
        }}>
          <div style={{ padding: '18px 20px 10px', overflowY: 'auto', minHeight: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#3E2723', marginBottom: '8px' }}>
              Some items still need a par
            </div>
            <div style={{ fontSize: '0.85rem', color: '#5A4A42', lineHeight: 1.55 }}>
              {confirmMissing.count} of {confirmMissing.total} item{confirmMissing.count !== 1 ? 's' : ''} still {confirmMissing.count !== 1 ? "don't" : "doesn't"} have a par. Continue without setting {confirmMissing.count !== 1 ? 'them' : 'it'}, or go back and finish?
            </div>
          </div>
          <div style={{ padding: '8px 20px 16px', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexShrink: 0 }}>
            <button
              onClick={() => setConfirmMissing(null)}
              style={{ fontSize: '0.84rem', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer' }}>
              Go back
            </button>
            <button
              onClick={() => { setConfirmMissing(null); doSave(); }}
              disabled={saving}
              style={{ fontSize: '0.84rem', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#1976D2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div onClick={handleBackdropClick} style={overlayStyle}>
      <style>{PARS_WIZARD_STYLES}</style>
      <div onClick={e => e.stopPropagation()} style={panelStyle}>

        {/* First-time setup banner */}
        {isFirstTimeSetup && (
          <div style={{ padding: '10px 18px', backgroundColor: '#EBF4FB', borderBottom: '1px solid #BDD7EF', flexShrink: 0, fontSize: '0.82rem', color: '#1A3A52', lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700 }}>Countertop-app setup.</span>{' '}You have not added pars to this template yet. This is something you do once for future use.
          </div>
        )}

        {/* Header */}
        <div style={{ padding: '16px 18px 12px', borderBottom: '1px solid #EDE6DE', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#3E2723' }}>Set par levels</div>
            {template?.name && (
              <div style={{ fontSize: '0.74rem', color: '#8B7B73', marginTop: '2px' }}>{template.name}</div>
            )}
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A89890', fontSize: '1.2rem', lineHeight: 1, padding: '2px 4px', marginLeft: '8px', flexShrink: 0 }}>✕</button>
        </div>

        {/* Explainer */}
        <div style={{ padding: '10px 18px', backgroundColor: '#F5F0EA', borderBottom: '1px solid #EDE6DE', flexShrink: 0, fontSize: '0.78rem', color: '#5A4A42', lineHeight: 1.55 }}>
          <span style={{ backgroundColor: PAR_BG, border: `1px solid ${PAR_BORDER}`, borderRadius: '3px', padding: '0 4px', fontWeight: 700 }}>PAR</span>
          {' '}= the amount you want on hand. It can be edited later from the template editor.
          <br />
          <span style={{ color: '#8B7B73', fontSize: '0.74rem' }}>Example: if par is 12 and you count 9.5, you need 2.5 — in the item&rsquo;s own unit. Which vendor to buy it from, and how their packages round, is decided later when you order.</span>
        </div>

        {/* Search + sort controls */}
        <div style={{ padding: '9px 18px', borderBottom: '1px solid #EDE6DE', flexShrink: 0, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            ref={searchRef}
            type="search"
            placeholder="Search items…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, padding: '6px 10px', borderRadius: '8px', border: '1px solid #D9CEC2', backgroundColor: '#fff', fontSize: '0.83rem', color: '#3E2723', outline: 'none' }}
          />
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #D9CEC2', backgroundColor: '#fff', fontSize: '0.82rem', color: '#5A4A42', cursor: 'pointer', outline: 'none' }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Column headers — 3-column grid: item | badge | par */}
        <div style={{ padding: '5px 18px', display: 'grid', gridTemplateColumns: ROW_GRID_COLUMNS, gap: '6px', backgroundColor: '#FAF6F2', borderBottom: '1px solid #EDE6DE', flexShrink: 0 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Item</div>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#A89890', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <span style={{ backgroundColor: PAR_BG, border: `1px solid ${PAR_BORDER}`, borderRadius: '3px', padding: '0 3px' }}>PAR</span>
          </div>
        </div>

        {/* Item rows — minHeight:0 is required so this flex child can shrink below its content
            size, keeping the footer/keypad pinned within the panel's max-height on mobile.
            The relative wrapper hosts the snap-back pill floating over the list. */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <div ref={rowListRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '2px 0' }}>
          {sortedFilteredRows.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: '#A89890', fontSize: '0.85rem' }}>
              No items found
            </div>
          ) : sortedFilteredRows.map(row => {
            const hasPar = !!row.parLevel && parseFloat(row.parLevel) > 0;
            // Location is suppressed for deduped rows (isDeduped=true) because it
            // represents multiple locations and showing one arbitrarily would mislead.
            const aliasHit = row.itemId != null ? aliasHitByItemId.get(row.itemId) : null;
            const metaParts = [row.category, !row.isDeduped ? row.location : null, vendorMetaFor(row, optionsByItemId)].filter(Boolean);
            const metaStr = truncateMeta(metaParts.join(' · '));
            const rowKey = row.id != null ? String(row.id) : `row-${row.name}-${row.category}-${row.location}`;

            const isParActive = isMobileKeypad && focusedField?.rowId === row.id && focusedField?.field === 'parLevel';

            return (
              <div
                key={rowKey}
                ref={el => { if (el) rowRefs.current[row.id] = el; else delete rowRefs.current[row.id]; }}
                style={{
                  display: 'grid', gridTemplateColumns: ROW_GRID_COLUMNS, gap: '6px',
                  padding: '7px 18px', borderBottom: '1px solid #F3EDE7', alignItems: 'center',
                  backgroundColor: hasPar ? '#fff' : '#FFFDF9',
                }}>
                {/* Item name + meta */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#3E2723', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.name}
                  </div>
                  {metaStr || aliasHit ? (
                    <div style={{ fontSize: '0.68rem', color: '#A89890', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {metaStr}
                      {aliasHit && (
                        <span style={{ color: '#8B7B73' }}>{metaStr ? ' · ' : ''}Matched vendor product: {aliasHit}</span>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Status badge — fixed column so all badges align */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                  {hasPar ? (
                    <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: '8px', backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9', whiteSpace: 'nowrap' }}>✓ Set</span>
                  ) : (
                    <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 5px', borderRadius: '8px', backgroundColor: '#FFF3E0', color: '#BF360C', border: '1px solid #FFCC80', whiteSpace: 'nowrap' }}>par</span>
                  )}
                </div>

                {/* PAR: number input (desktop) or tap-to-activate display (mobile) */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {isMobileKeypad ? (
                    <button
                      onPointerDown={e => { e.preventDefault(); activateField(row.id, 'parLevel'); }}
                      className={isParActive ? 'pars-wizard-field-active' : ''}
                      style={{
                        width: '52px', padding: '5px 5px', borderRadius: '6px',
                        border: isParActive ? `2px solid #1976D2` : `1px solid ${hasPar ? PAR_BORDER : '#FFCC80'}`,
                        backgroundColor: PAR_BG, fontSize: '0.82rem', color: row.parLevel ? '#3E2723' : '#C8A86B',
                        textAlign: 'right', outline: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', fontWeight: row.parLevel ? 400 : 400,
                        minHeight: '30px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      }}
                    >
                      {row.parLevel || '0'}
                    </button>
                  ) : (
                    <input
                      type="number"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      min="0"
                      step="any"
                      placeholder="0"
                      className="pars-wizard-par-input"
                      ref={el => { const k = `${row.id}:parLevel`; if (el) inputRefs.current[k] = el; else delete inputRefs.current[k]; }}
                      value={row.parLevel}
                      onChange={e => updateRow(row.id, 'parLevel', e.target.value)}
                      onFocus={handleInputFocus}
                      style={{ width: '52px', padding: '5px 5px', borderRadius: '6px', border: `1px solid ${hasPar ? PAR_BORDER : '#FFCC80'}`, backgroundColor: PAR_BG, fontSize: '0.82rem', color: '#3E2723', textAlign: 'right', outline: 'none' }}
                    />
                  )}
                  <span style={{ fontSize: '0.68rem', color: '#7A6A62', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '52px' }}>
                    {row.parUnit || 'each'}
                  </span>
                </div>

              </div>
            );
          })}
        </div>

        {/* Snap-back pill — floats over the list while the anchor item is
            scrolled out of view; one tap re-centers the list on it so the
            user can never lose the item they were counting. */}
        {anchorRowRendered && anchorOffscreen && anchorRow && (
          <button
            type="button"
            onPointerDown={e => { e.preventDefault(); snapBackToAnchor(); }}
            style={{
              position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)',
              maxWidth: 'calc(100% - 32px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              padding: '7px 14px', borderRadius: '999px', border: '1px solid #4A3A32',
              backgroundColor: '#5A4A42', color: '#FFFFFF', fontSize: '0.76rem', fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,0.25)',
              touchAction: 'manipulation', userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >
            ↩ Back to {truncateMeta(anchorRow.name, 26)}
          </button>
        )}
        </div>

        {/* Footer — hidden on mobile (Skip/Save move into the keypad header) */}
        {!isMobileKeypad && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid #EDE6DE', display: 'flex', justifyContent: hideSkip ? 'flex-end' : 'space-between', alignItems: 'center', flexShrink: 0, backgroundColor: '#FDFBF7' }}>
            {!hideSkip && (
            <button
              onClick={onSkip}
              disabled={saving}
              style={{ fontSize: '0.83rem', fontWeight: 500, padding: '7px 14px', borderRadius: '8px', border: '1px solid #D4C4B0', backgroundColor: 'transparent', color: '#8B7B73', cursor: saving ? 'not-allowed' : 'pointer' }}>
              Skip for now
            </button>
            )}
            <button
              onClick={handleSaveClick}
              disabled={saving}
              style={{ fontSize: '0.84rem', fontWeight: 700, padding: '7px 22px', borderRadius: '8px', border: 'none', backgroundColor: '#1976D2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {/* ── Mobile numeric keypad ──────────────────────────────────────────────
            Shown only on touch-primary devices (isMobileKeypad). The keypad is
            docked at the bottom of the panel; the item-row list scrolls above it.
            Visual language matches the CountInterface counting keypad. ───────── */}
        {isMobileKeypad && (
          <div style={{ flexShrink: 0, borderTop: '2px solid #EDE6DE', backgroundColor: '#F5F0EA' }}>

            {/* Keypad header: current field indicator + Skip/Save buttons */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', gap: '8px', borderBottom: '1px solid #EDE6DE' }}>
              {/* Current field display */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                {focusedField && focusedRow ? (
                  <>
                    <span style={{
                      fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '4px',
                      backgroundColor: PAR_BG,
                      border: `1px solid ${PAR_BORDER}`,
                      color: '#3E2723', flexShrink: 0,
                    }}>
                      PAR
                    </span>
                    <span style={{ fontSize: '1.15rem', fontWeight: 700, color: '#3E2723', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
                      {focusedValue !== '' ? focusedValue : <span style={{ color: '#C8B5A0', fontWeight: 400, fontSize: '1rem' }}>0</span>}
                    </span>
                    <span style={{ fontSize: '0.68rem', color: '#A89890', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60px' }}>
                      {focusedUnit}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#A89890' }}>
                    Tap <span style={{ backgroundColor: PAR_BG, border: `1px solid ${PAR_BORDER}`, borderRadius: '3px', padding: '0 3px', fontWeight: 700, color: '#3E2723' }}>PAR</span>
                    {' '}to enter a value
                  </span>
                )}
              </div>

              {/* Next field button — shown when a field is active */}
              {focusedField && (
                <button
                  onPointerDown={e => { e.preventDefault(); moveToNextField(); }}
                  style={{ fontSize: '0.75rem', fontWeight: 600, padding: '5px 10px', borderRadius: '8px', border: '1px solid #D4C4B0', backgroundColor: '#fff', color: '#5A4A42', cursor: 'pointer', flexShrink: 0 }}>
                  Next ›
                </button>
              )}

              {/* Skip / Save */}
              {!hideSkip && (
              <button
                onClick={onSkip}
                disabled={saving}
                style={{ fontSize: '0.75rem', fontWeight: 500, padding: '5px 10px', borderRadius: '8px', border: '1px solid #D4C4B0', backgroundColor: 'transparent', color: '#8B7B73', cursor: saving ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                Skip
              </button>
              )}
              <button
                onClick={handleSaveClick}
                disabled={saving}
                style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#1976D2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, flexShrink: 0 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>

            {/* 3×4 keypad grid: 1–9, decimal, 0, backspace */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '6px',
              padding: '8px 10px 10px',
            }}>
              {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map(key => (
                <button
                  key={key}
                  onPointerDown={e => { e.preventDefault(); handleKeypadPress(key); }}
                  style={{
                    height: '46px',
                    borderRadius: '8px',
                    border: '2px solid #C8B5A0',
                    backgroundColor: key === '⌫' ? '#F8F1E8' : '#FFFFFF',
                    color: key === '⌫' ? '#6D5648' : '#3D2518',
                    fontSize: key === '⌫' ? '1rem' : '1.3rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: 'manipulation',
                    opacity: !focusedField && key !== '⌫' ? 0.5 : 1,
                  }}
                >
                  {key === '⌫' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.25" style={{ display: 'inline-block' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 6H9a2 2 0 00-1.414.586L3.586 10.586A2 2 0 003 12a2 2 0 00.586 1.414l4 4A2 2 0 009 18h9a2 2 0 002-2V8a2 2 0 00-2-2z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10l-3 4M10 10l3 4"/>
                    </svg>
                  ) : key}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
